import { Box, FormControl, FormLabel, Heading, Switch, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type {
  FoaPart,
  ProcedureType,
  Regulation,
  SourcingEventDto,
  SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { offersStagedReduction, stagedReductionProvision } from "../../../../../common/procurement/procedure";

interface Props {
  eventId: string;
  procedureType: ProcedureType | null | undefined;
  regulation: Regulation | null | undefined;
  foaPart: FoaPart | null | undefined;
  reducesInSuccessiveStages: boolean | null;
  isEditable: boolean;
}

export const StagedReductionField = ({
  eventId,
  procedureType,
  regulation,
  foaPart,
  reducesInSuccessiveStages,
  isEditable,
}: Props) => {
  const { t } = useTranslation();
  const { updateField } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);

  if (!offersStagedReduction(procedureType)) return null;
  const provision = stagedReductionProvision({ regulation, foaPart });

  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" bg="white" p="5">
      <Heading as="h3" size="sm">
        {t("Reduction in stages")}
      </Heading>
      <FormControl mt="4" display="flex" alignItems="flex-start" justifyContent="space-between" gap="6">
        <Box>
          <FormLabel htmlFor="reducesInSuccessiveStages" mb="1" cursor="pointer">
            <Text fontSize="sm" fontWeight="semibold">
              {t("Reserve the right to reduce the number of tenders or solutions in stages")}
            </Text>
          </FormLabel>
          <Text fontSize="sm" color="smMuted">
            {reducesInSuccessiveStages
              ? provision
                ? t(
                    "Between the negotiation rounds you may take suppliers out on the award criteria, with a reason. This is stated in the notice ({{provision}}).",
                    { provision },
                  )
                : t(
                    "Between the negotiation rounds you may take suppliers out on the award criteria, with a reason. This is stated in the notice.",
                  )
              : t("Every supplier stays in the negotiations until it is rejected or withdraws.")}
          </Text>
        </Box>
        <Switch
          id="reducesInSuccessiveStages"
          isChecked={reducesInSuccessiveStages === true}
          isDisabled={!isEditable}
          onChange={(e) => updateField({ publicContext: { reducesInSuccessiveStages: e.target.checked } })}
          colorScheme="teal"
          mt="1"
        />
      </FormControl>
    </Box>
  );
};
